(function () {
  'use strict';

  var root = document.querySelector('[data-track]');
  if (!root) return;

  var API = root.getAttribute('data-api');
  var GEO_URL = root.getAttribute('data-geo');

  var VB_W = 960, VB_H = 600, PAD = 24;
  var RAD = Math.PI / 180;

  function albersFactory(lam0, phi0, phi1, phi2) {
    var n = (Math.sin(phi1 * RAD) + Math.sin(phi2 * RAD)) / 2;
    var C = Math.cos(phi1 * RAD) * Math.cos(phi1 * RAD) + 2 * n * Math.sin(phi1 * RAD);
    var rho0 = Math.sqrt(C - 2 * n * Math.sin(phi0 * RAD)) / n;
    return function (lon, lat) {
      var theta = n * ((lon - lam0) * RAD);
      var v = C - 2 * n * Math.sin(lat * RAD);
      if (v < 0) return null;
      var rho = Math.sqrt(v) / n;
      return [rho * Math.sin(theta), rho * Math.cos(theta) - rho0];
    };
  }

  var projConus = albersFactory(-96, 37.5, 29.5, 45.5);
  var projAK = albersFactory(-152, 60, 55, 65);
  var projHI = albersFactory(-157, 20, 8, 18);

  var INSET = { AK: null, HI: null };
  var conusFit = null;
  var geo = null;

  function fitConus(states) {
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    Object.keys(states).forEach(function (code) {
      if (code === 'AK' || code === 'HI') return;
      states[code].forEach(function (ring) {
        ring.forEach(function (p) {
          var q = projConus(p[0], p[1]);
          if (!q) return;
          if (q[0] < minX) minX = q[0];
          if (q[0] > maxX) maxX = q[0];
          if (q[1] < minY) minY = q[1];
          if (q[1] > maxY) maxY = q[1];
        });
      });
    });
    var s = Math.min((VB_W - PAD * 2) / (maxX - minX), (VB_H - PAD * 2) / (maxY - minY));
    return {
      s: s,
      tx: PAD + (VB_W - PAD * 2 - (maxX - minX) * s) / 2 - minX * s,
      ty: PAD + (VB_H - PAD * 2 - (maxY - minY) * s) / 2 - minY * s
    };
  }

  function fitInset(states, code, proj, scale, boxX, boxY, boxW) {
    var pts = [];
    (states[code] || []).forEach(function (ring) {
      ring.forEach(function (p) { var q = proj(p[0], p[1]); if (q) pts.push(q); });
    });
    if (!pts.length) return null;
    var minX = Math.min.apply(null, pts.map(function (p) { return p[0]; }));
    var maxX = Math.max.apply(null, pts.map(function (p) { return p[0]; }));
    var minY = Math.min.apply(null, pts.map(function (p) { return p[1]; }));
    var s = (boxW / (maxX - minX)) * scale;
    return { s: s, tx: boxX - minX * s, ty: boxY - minY * s, proj: proj };
  }

  function project(lon, lat, state) {
    var st = (state || '').toUpperCase();
    var t = st === 'AK' ? INSET.AK : st === 'HI' ? INSET.HI : null;
    if (t) {
      var q = t.proj(lon, lat);
      return q ? [q[0] * t.s + t.tx, q[1] * t.s + t.ty] : null;
    }
    if (!conusFit) return null;
    var c = projConus(lon, lat);
    return c ? [c[0] * conusFit.s + conusFit.tx, c[1] * conusFit.s + conusFit.ty] : null;
  }

  function ns(tag) { return document.createElementNS('http://www.w3.org/2000/svg', tag); }

  function drawBase(svg) {
    var g = ns('g');
    g.setAttribute('class', 'trk__states');
    Object.keys(geo).forEach(function (code) {
      geo[code].forEach(function (ring) {
        var d = '';
        for (var i = 0; i < ring.length; i++) {
          var q = project(ring[i][0], ring[i][1], code);
          if (!q) continue;
          d += (d ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1);
        }
        if (!d) return;
        var path = ns('path');
        path.setAttribute('d', d + 'Z');
        path.setAttribute('class', 'trk__state');
        g.appendChild(path);
      });
    });
    svg.appendChild(g);
  }

  function drawRoute(svg, stops) {
    if (stops.length < 1) return;
    var line = ns('path');
    var d = '';
    stops.forEach(function (s) { d += (d ? 'L' : 'M') + s.x.toFixed(1) + ' ' + s.y.toFixed(1); });
    line.setAttribute('d', d);
    line.setAttribute('class', 'trk__route');
    svg.appendChild(line);

    stops.forEach(function (s, i) {
      var last = i === stops.length - 1;
      var dot = ns('circle');
      dot.setAttribute('cx', s.x.toFixed(1));
      dot.setAttribute('cy', s.y.toFixed(1));
      dot.setAttribute('r', last ? 6 : 3.5);
      dot.setAttribute('class', 'trk__stop' + (last ? ' trk__stop--now' : '') + (s.approx ? ' trk__stop--approx' : ''));
      var t = ns('title');
      t.textContent = s.label + (s.approx ? ' (approximate)' : '');
      dot.appendChild(t);
      svg.appendChild(dot);
    });
  }

  function renderMap(data) {
    var wrap = root.querySelector('[data-track-map]');
    if (!wrap || !geo) return;
    wrap.innerHTML = '';
    var svg = ns('svg');
    svg.setAttribute('viewBox', '0 0 ' + VB_W + ' ' + VB_H);
    svg.setAttribute('class', 'trk__svg');
    svg.setAttribute('role', 'img');

    var stops = [];
    var events = (data.events || []).slice().reverse();
    var seen = {};
    events.forEach(function (e) {
      var L = e.location;
      if (!L || L.lat === null || L.lat === undefined) return;
      var key = L.lat + ',' + L.lon;
      if (seen[key]) return;
      seen[key] = true;
      var q = project(L.lon, L.lat, L.state);
      if (q) stops.push({ x: q[0], y: q[1], label: L.city + ', ' + L.state, approx: !!L.approx });
    });
    var dest = data.destination;
    if (dest && dest.lat !== null && dest.lat !== undefined) {
      var dq = project(dest.lon, dest.lat, dest.state);
      var dkey = dest.lat + ',' + dest.lon;
      if (dq && !seen[dkey]) stops.push({ x: dq[0], y: dq[1], label: dest.city + ', ' + dest.state, approx: !!dest.approx });
    }

    svg.setAttribute('aria-label', stops.length
      ? 'Route map, ' + stops.length + ' scan locations'
      : 'Route map, no locations yet');

    drawBase(svg);
    drawRoute(svg, stops);
    wrap.appendChild(svg);
    wrap.hidden = stops.length === 0;
  }

  var STATUS_LABEL = {
    pre_transit: 'Label created', in_transit: 'On its way', out_for_delivery: 'Out for delivery',
    delivered: 'Delivered', available_for_pickup: 'Ready for pickup', return_to_sender: 'Returning to sender',
    failure: 'Delivery problem', cancelled: 'Cancelled', error: 'Tracking problem', unknown: 'Tracking pending'
  };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fmtDate(v) {
    if (!v) return '';
    var d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function fmtEta(v) {
    if (!v) return '';
    var m = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00))?$/.exec(String(v));
    if (m) return new Date(Date.UTC(+m[1], m[2] - 1, +m[3])).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    return fmtDate(v);
  }

  function fmtTime(v) {
    if (!v) return '';
    var d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  function render(data) {
    var status = data.status || 'unknown';
    root.querySelector('[data-track-status]').textContent = STATUS_LABEL[status] || 'Tracking pending';

    var eta = root.querySelector('[data-track-eta]');
    if (status === 'delivered') {
      var last = (data.events || [])[0];
      eta.textContent = last && last.datetime ? 'Delivered ' + fmtDate(last.datetime) : 'Delivered';
    } else if (data.est_delivery_date) {
      eta.textContent = 'Estimated ' + fmtEta(data.est_delivery_date);
    } else {
      eta.textContent = 'No estimate yet';
    }

    var meta = root.querySelector('[data-track-meta]');
    var bits = [];
    if (data.order_name) bits.push(esc(data.order_name));
    if (data.carrier) bits.push(esc(data.carrier));
    if (data.tracking_code) bits.push(esc(data.tracking_code));
    meta.innerHTML = bits.join(', ');

    var tl = root.querySelector('[data-track-timeline]');
    tl.innerHTML = (data.events || []).map(function (e) {
      var L = e.location;
      var place = L && L.city ? esc(L.city + ', ' + L.state) : '';
      return '<li class="trk__event">' +
        '<span class="trk__event-when"><span class="trk__event-date">' + esc(fmtDate(e.datetime)) +
        '</span><span class="trk__event-time">' + esc(fmtTime(e.datetime)) + '</span></span>' +
        '<span class="trk__event-msg">' + esc(e.message || '') +
        (place ? '<span class="trk__event-place">' + place + '</span>' : '') +
        '</span></li>';
    }).join('') || '<li class="trk__event trk__event--empty">No scans yet. The carrier posts the first one when they collect it.</li>';

    renderReport(data);
    renderMap(data);
    root.querySelector('[data-track-form]').hidden = true;
    root.querySelector('[data-track-result]').hidden = false;
    root.querySelector('[data-track-result]').focus();
    var rep = autoReport && root.querySelector('[data-track-report]:not([hidden]) [data-rep-open]');
    autoReport = false;
    if (rep) rep.click();
  }

  var DAY = 864e5;
  var autoReport = new URLSearchParams(location.search).get('report') === '1';

  function reportState(data) {
    var forced = new URLSearchParams(location.search).get('demo');
    var S = window.Shopify;
    var testable = S && (S.designMode || (S.theme && S.theme.role && S.theme.role !== 'main'));
    if (forced && testable) return forced;
    var status = data.status;
    if (status === 'delivered') return 'delivered';
    if (status === 'pre_transit') {
      var first = (data.events || [])[0];
      var t = first && first.datetime ? new Date(first.datetime).getTime() : 0;
      return t && (Date.now() - t) > 3 * DAY ? 'stuck' : null;
    }
    if (data.est_delivery_date) {
      var eta = new Date(data.est_delivery_date).getTime();
      if (eta && (Date.now() - eta) > 2 * DAY) return 'late';
    }
    return null;
  }

  var PROMPTS = {
    delivered: { options: ['Send a replacement', 'Refund my order'] },
    stuck: { options: ['Send a replacement'] },
    late: { options: ['Send a replacement', 'Refund my order'] }
  };

  function renderReport(data) {
    var wrap = root.querySelector('[data-track-report]');
    var state = reportState(data);
    if (!wrap) return;
    if (!state || !PROMPTS[state]) { wrap.hidden = true; return; }
    var p = PROMPTS[state];
    wrap.hidden = false;
    wrap.innerHTML =
      '<button class="al-btn trk__rep-open" type="button" data-rep-open>Report a problem</button>' +
      '<p class="trk__rep-note">This form goes directly to me <em>(Alex, the owner)</em>, and I\'ll take care of it as soon as I can!</p>' +
      '<form class="trk__rep-form" data-rep-form hidden>' +
        '<label class="trk__label" for="rep-email">Your email</label>' +
        '<input class="trk__input" id="rep-email" name="email" type="email" autocomplete="email" required>' +
        '<label class="trk__label" for="rep-name">Your name</label>' +
        '<input class="trk__input" id="rep-name" name="customer_name" type="text" autocomplete="name" required>' +
        '<label class="trk__label" for="rep-notes">Additional details</label>' +
        '<textarea class="trk__input trk__rep-notes" id="rep-notes" name="notes" rows="4" required minlength="5"></textarea>' +
        '<label class="trk__label" for="rep-res">Preferred resolution</label>' +
        '<span class="trk__rep-selwrap">' +
          '<select class="trk__input trk__rep-select" id="rep-res" name="resolution" required>' +
            p.options.map(function (o) { return '<option value="' + esc(o) + '">' + esc(o) + '</option>'; }).join('') +
          '</select>' +
          '<svg class="trk__rep-caret" viewBox="0 0 10 6" aria-hidden="true"><polygon points="0,0 10,0 5,6"/></svg>' +
        '</span>' +
        '<button class="al-btn trk__rep-send" type="submit">Send report</button>' +
        '<p class="trk__rep-msg" data-rep-msg role="status" hidden></p>' +
      '</form>';

    wrap.querySelector('[data-rep-open]').addEventListener('click', function () {
      var frm = wrap.querySelector('[data-rep-form]');
      frm.hidden = false;
      this.hidden = true;
      frm.querySelector('#rep-email').focus();
    });

    wrap.querySelector('[data-rep-form]').addEventListener('submit', function (ev) {
      ev.preventDefault();
      var frm = this, msg = frm.querySelector('[data-rep-msg]');
      var btn = frm.querySelector('.trk__rep-send');
      btn.disabled = true;
      msg.hidden = false;
      msg.className = 'trk__rep-msg';
      msg.textContent = 'Sending…';
      fetch(API + '/api/tracking/issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: frm.email.value.trim(),
          tracking_code: data.tracking_code,
          customer_name: frm.customer_name.value.trim(),
          notes: frm.notes.value.trim(),
          resolution: frm.resolution.value ? [frm.resolution.value] : []
        })
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (b) {
          if (!r.ok) { var er = new Error(b && b.error ? b.error : 'Unable to send that report.'); er.userMessage = true; throw er; }
          return b;
        });
      }).then(function (b) {
        frm.innerHTML = '<p class="trk__rep-msg trk__rep-msg--ok">' +
          esc(b.message || 'Report sent. You will get a reply within 24 hours.') + '</p>';
      }).catch(function (e) {
        btn.disabled = false;
        msg.className = 'trk__rep-msg trk__rep-msg--err';
        msg.textContent = e && e.userMessage ? e.message : 'Unable to send that report.';
      });
    });
  }

  function setBusy(on) { root.querySelector('[data-track-busy]').hidden = !on; }
  function showError(msg) {
    var e = root.querySelector('[data-track-error]');
    e.textContent = msg;
    e.hidden = false;
  }
  function clearError() { root.querySelector('[data-track-error]').hidden = true; }

  function ga(name, params) {
    if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
  }

  function loadGeo() {
    if (geo) return Promise.resolve(geo);
    return fetch(GEO_URL).then(function (r) { return r.json(); }).then(function (g) {
      geo = g;
      conusFit = fitConus(geo);
      INSET.AK = fitInset(geo, 'AK', projAK, 1, PAD, VB_H - 150, 200);
      INSET.HI = fitInset(geo, 'HI', projHI, 1, PAD + 230, VB_H - 92, 110);
      return geo;
    }).catch(function () {
      geo = null;
      return null;
    });
  }

  function go(url, opts, method) {
    setBusy(true); clearError();
    ga('track_lookup', { lookup_method: method });
    return Promise.all([loadGeo(), fetch(url, opts)])
      .then(function (r) {
        var res = r[1];
        return res.json().catch(function () { return {}; }).then(function (body) {
          if (!res.ok) { var er = new Error(body && body.error ? body.error : 'Unable to find that. Please check and try again.'); er.userMessage = true; throw er; }
          return body;
        });
      })
      .then(function (data) {
        setBusy(false);
        if (data.boxes) {
          if (!data.boxes.length) {
            ga('track_result', { lookup_method: method, lookup_result: 'no_tracking_yet', box_count: 0 });
            showError('No tracking for this order yet. Please check back soon.'); return;
          }
          ga('track_result', { lookup_method: method, lookup_result: data.boxes[0].status || 'unknown', box_count: data.boxes.length });
          render(data.boxes[0]);
          if (data.boxes.length > 1) renderOthers(data.boxes);
          return;
        }
        ga('track_result', { lookup_method: method, lookup_result: data.status || 'unknown', box_count: 1 });
        render(data);
      })
      .catch(function (err) {
        setBusy(false);
        ga('track_result', { lookup_method: method, lookup_result: err && err.userMessage ? 'not_found' : 'error', box_count: 0 });
        showError(err && err.userMessage ? err.message : 'Unable to reach the tracking service. Please try again.');
      });
  }

  function renderOthers(boxes) {
    var el = root.querySelector('[data-track-others]');
    el.innerHTML = '<p class="trk__others-label">This order shipped in ' + boxes.length + ' packages</p>' +
      boxes.map(function (b, i) {
        return '<button type="button" class="trk__otherbtn" data-box="' + i + '">Package ' + (i + 1) +
          ', ' + esc(STATUS_LABEL[b.status] || 'Pending') + '</button>';
      }).join('');
    el.hidden = false;
    Array.prototype.forEach.call(el.querySelectorAll('[data-box]'), function (b) {
      b.addEventListener('click', function () { render(boxes[+b.getAttribute('data-box')]); });
    });
  }

  root.querySelector('[data-form-code]').addEventListener('submit', function (ev) {
    ev.preventDefault();
    var v = this.querySelector('input').value.replace(/[\s-]+/g, '');
    if (v) go(API + '/api/tracking/' + encodeURIComponent(v), undefined, 'code');
  });

  root.querySelector('[data-form-order]').addEventListener('submit', function (ev) {
    ev.preventDefault();
    var num = this.querySelector('[name=order]').value.trim().replace(/^#/, '').trim();
    var em = this.querySelector('[name=email]').value.trim();
    if (num && em) {
      go(API + '/api/tracking/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_number: num, email: em })
      }, 'order');
    }
  });

  root.querySelector('[data-track-again]').addEventListener('click', function () {
    root.querySelector('[data-track-result]').hidden = true;
    root.querySelector('[data-track-others]').hidden = true;
    root.querySelector('[data-track-form]').hidden = false;
    clearError();
  });

  var qs = new URLSearchParams(location.search);
  var oid = qs.get('order'), tok = qs.get('t'), LINK_KEY = 'al-trk-link';
  var linkCode = qs.get('code') || qs.get('id');
  if (oid && tok) {
    qs.delete('order'); qs.delete('t');
    try { var rest = qs.toString(); history.replaceState(null, '', location.pathname + (rest ? '?' + rest : '') + location.hash); } catch (e) {}
    try { sessionStorage.setItem(LINK_KEY, JSON.stringify({ o: oid, t: tok })); } catch (e) {}
  } else if (!linkCode) {
    try { var saved = JSON.parse(sessionStorage.getItem(LINK_KEY) || 'null'); if (saved && saved.o && saved.t) { oid = saved.o; tok = saved.t; } } catch (e) {}
  }
  if (oid && tok) {
    go(API + '/api/tracking/by-order/' + encodeURIComponent(oid) +
       '?t=' + encodeURIComponent(tok), undefined, 'email_link');
  } else if (linkCode) {
    var qc = linkCode.replace(/[\s-]+/g, '');
    root.querySelector('[data-form-code] input').value = qc;
    if (qc) go(API + '/api/tracking/' + encodeURIComponent(qc), undefined, 'code_link');
  }
})();
